import { useReducer } from "react";
import BinContext, { internalState } from "./context";
import reducer from "./reducer";
import { execute } from "../../../utils/helpers/execute";
import EndPoints from "../../../services/end-points";
import { IBin, IBinDetails } from "../../../models/wms/bin/response";
import { ICreateBin, IUpdateBin } from "../../../models/wms/bin/request";
import { IBinQuery } from "../../../models/wms/bin/query";

export interface IProps {
  children: React.ReactNode;
}

const BinContextProvider: React.FC<IProps> = (props) => {
  const [state, dispatch] = useReducer(reducer, internalState);

  const getData = async () => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "list" } });

        const { data } = await EndPoints.wmsLayout.getAllBins(state.query);

        dispatch({ type: "SET_LIST", payload: { list: data } });
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "list" } });
      },
      throwException: false,
    });
  };

  const getDetails = async (id: number) => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "details" } });

        const { data } = await EndPoints.wmsLayout.getBin(id);
        dispatch({ type: "SET_DETAILS", payload: { details: data } });
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "details" } });
      },
      throwException: false,
    });
  };

  const setDetails = async (data?: IBinDetails) => {
    dispatch({ type: "SET_DETAILS", payload: { details: data } });
  };

  const createBin = async (request: ICreateBin) => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "create" } });

        await EndPoints.wmsLayout.createBin(request);
        getData();
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "create" } });
      },
      throwException: true,
    });
  };

  const updateBin = async (id: number, request: IUpdateBin) => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "update" } });
        await EndPoints.wmsLayout.updateBin(id, request);

        getData();
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "update" } });
      },
      throwException: true,
    });
  };

  const deleteBin = async (id: number) => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "delete" } });

        await EndPoints.wmsLayout.deleteBin(id);

        getData();
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "delete" } });
      },
      throwException: true,
    });
  };

  const reorderBin = async (id: number, pickSequence: number) => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "update" } });
        await EndPoints.wmsLayout.reorderBin(id, pickSequence);
        getData();
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "update" } });
      },
      throwException: true,
    });
  };

  const setSearch = (search?: string) => {
    dispatch({ type: "SET_SEARCH", payload: { search } });
  };

  const setQuery = (query: IBinQuery) => {
    dispatch({ type: "SET_QUERY", payload: { query } });
  };

  return (
    <BinContext.Provider
      value={{
        ...state,
        actions: {
          getData,
          getDetails,
          setDetails,
          createBin,
          updateBin,
          deleteBin,
          reorderBin,
          setSearch,
          setQuery,
        },
      }}
    >
      {props.children}
    </BinContext.Provider>
  );
};

export default BinContextProvider;
